import React from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';

import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faBagShopping,
  faCalendar,
  faCheckCircle,
  faCircleExclamation,
  faCreditCard,
  faDesktop,
  faDoorOpen,
  faEllipsis,
  faEye,
  faGear,
  faLink,
  faMagnifyingGlass,
  faMoneyBill,
  faPencil,
  faRightFromBracket,
  faStar,
  faTableColumns,
  faTrashCan,
  faTriangleExclamation,
  faUserPlus,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { Toaster } from '@/components/ui/sonner';
import { Toast } from '../shared/components/Toast';
import { AuthProvider } from '../shared/context/Auth';
import { ToastProvider } from '../shared/context/Toast';
import SidebarLayout from './layouts/SidebarLayout';
import {
  ForgotPassword,
  Login,
  PrivateRoute,
  RequestUnlock,
  ResetPassword,
  Signup,
  Unlock,
} from './pages/Auth';
import Billing from './pages/Billing';
import Calendar from './pages/Calendar';
import Conversation from './pages/Conversation';
import Messenger from './pages/Messenger';
import {
  Checkout,
  PaymentsDashboard,
  PaymentsOnboarding,
  Payouts,
} from './pages/Payments';
import People from './pages/People';
import { Lists as ReviewLists, Settings as ReviewSettings } from './pages/Reviews';
import Settings from './pages/Settings';
import Website from './pages/Website';
import Workflow from './pages/Workflow';
import * as CONSTANTS from './shared/constants';
import { NavProvider } from './shared/context/Nav';

library.add(
  faBagShopping,
  faCalendar,
  faCheckCircle,
  faCircleExclamation,
  faCreditCard,
  faDesktop,
  faDoorOpen,
  faEllipsis,
  faEye,
  faGear,
  faLink,
  faMagnifyingGlass,
  faMoneyBill,
  faPencil,
  faRightFromBracket,
  faStar,
  faTableColumns,
  faTrashCan,
  faTriangleExclamation,
  faUserPlus,
  faUsers
);

const stripePromise = loadStripe(`${import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY}`);

const Workspace = () => {
  return (
    <Elements stripe={stripePromise}>
      <AuthProvider entityType="user">
        <ToastProvider>
          <Toast />
          <Toaster expand richColors />

          <Router>
            <NavProvider>
              <Routes>
                <Route path={CONSTANTS.FORGOT_PASSWORD} element={<ForgotPassword />} />
                <Route path={CONSTANTS.LOGIN} element={<Login />} />
                <Route path={CONSTANTS.REQUEST_UNLOCK} element={<RequestUnlock />} />
                <Route path={CONSTANTS.RESET_PASSWORD} element={<ResetPassword />} />
                <Route path={CONSTANTS.SIGNUP} element={<Signup />} />
                <Route path={CONSTANTS.UNLOCK} element={<Unlock />} />

                <Route element={<PrivateRoute />}>
                  <Route element={<SidebarLayout />}>
                    <Route path={CONSTANTS.BILLING} element={<Billing />} />
                    <Route path={CONSTANTS.CALENDAR} element={<Calendar />} />
                    <Route path={CONSTANTS.CHECKOUT} element={<Checkout />} />
                    {/* TODO: Can Conversation be removed? */}
                    <Route path={CONSTANTS.CONVERSATION} element={<Conversation />} />
                    <Route path={CONSTANTS.MESSENGER} element={<Messenger />} />
                    <Route path={CONSTANTS.PAYMENTS} element={<PaymentsDashboard />} />

                    <Route
                      path={CONSTANTS.PAYMENTS_ONBOARDING}
                      element={<PaymentsOnboarding />}
                    />

                    <Route path={CONSTANTS.PAYOUTS} element={<Payouts />} />
                    <Route path={CONSTANTS.PEOPLE} element={<People />} />
                    <Route path={CONSTANTS.REVIEWS} element={<ReviewLists />} />

                    <Route
                      path={CONSTANTS.REVIEW_SETTINGS}
                      element={<ReviewSettings />}
                    />

                    <Route path={CONSTANTS.SETTINGS} element={<Settings />} />
                    <Route path={CONSTANTS.WEBSITE} element={<Website />} />
                    <Route path={CONSTANTS.WORKFLOW} element={<Workflow />} />
                  </Route>
                </Route>
              </Routes>
            </NavProvider>
          </Router>
        </ToastProvider>
      </AuthProvider>
    </Elements>
  );
};

export default Workspace;
